import GridTable from "@components/grid-table"
import { useMarketplaceOrdersPageContext } from "@contexts/main/marketplace_orders_page_context"
import { getMarketplaceOrdersQuery } from "@domain/marketplace_orders"
import SearchIcon from "@mui/icons-material/Search"
import { Box, Button, InputAdornment, Stack, TextField } from "@mui/material"
import type {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams
} from "@mui/x-data-grid"
import { useQuery } from "@tanstack/react-query"
import { useDebounce } from "@uidotdev/usehooks"
import { formatDate } from "@utils/functions/formatDate"
import { showErrorToast } from "@utils/toast"
import {
  MarketplaceBuyer,
  MarketplaceOrderItem,
  MarketplaceOrderShipmentTracking,
  MarketplaceOrderShippingAdress,
  MarketplaceOrderShippingService,
  MarketplaceOrdersPaginated,
  MarketplaceType
} from "__generated__/graphql"
import { useEffect, useState } from "react"

export interface SanitizedMarketplaceOrder {
  _id: string
  orderId: string
  type: string
  orderStatus: string
  amountPaid: string
  currency: string
  items: MarketplaceOrderItem[]
  itemsLength: number
  buyer: MarketplaceBuyer
  cancelStatus: string
  checkoutStatus: string
  isTaxIncluded: boolean
  taxRate: string
  paymentStatus: string
  returnStatus: string
  refundStatus: string
  seller: string
  shipmentTracking?: MarketplaceOrderShipmentTracking
  shippingAddress?: MarketplaceOrderShippingAdress
  shippingService?: MarketplaceOrderShippingService
  createdAt: Date
  updatedAt: Date
}

export interface SanitizedMarketplaceOrdersResponse {
  orders: SanitizedMarketplaceOrder[]
  total: number
}

interface Props {
  onSelectOrder: (callSid: string) => void
}

export default function MarketplaceOrderList({ onSelectOrder }: Props) {
  const { translate, filters } = useMarketplaceOrdersPageContext()
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100
  })
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search, 500)

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }))
  }, [debouncedSearch])

  const { data, isLoading } = useQuery({
    queryKey: [
      "marketplace-orders-list",
      pagination.page,
      pagination.pageSize,
      filters,
      debouncedSearch
    ],
    queryFn: async () => {
      const response = await getMarketplaceOrdersQuery({
        input: {
          customerId:
            filters?.customerId === "ALL" ? undefined : filters?.customerId,
          type:
            filters?.marketplaceType === "ALL"
              ? undefined
              : (filters?.marketplaceType as MarketplaceType),
          pageNumber: pagination.page + 1,
          pageSize: pagination.pageSize,
          buyerId: debouncedSearch
        }
      })

      if (!response.isSuccess || !response.data) {
        showErrorToast(
          response.errorMessage ??
            translate("error_fetching_logs") ??
            "Error fetching logs"
        )
        return { orders: [], total: 0 }
      }
      const sanitizedData = sanitizeMarketplaceOrders(response.data)
      return sanitizedData
    },
    enabled: !!filters.customerId
  })

  const sanitizeMarketplaceOrders = (
    rawData: MarketplaceOrdersPaginated
  ): SanitizedMarketplaceOrdersResponse => {
    return {
      orders: rawData.orders.map((order) => ({
        _id: order._id,
        orderId: order.orderId || "-",
        type: order.type || "-", // Handling null subject
        orderStatus: order.orderStatus || "-",
        amountPaid: order.amountPaid,
        currency: order.currency || "-",
        items: order.items,
        itemsLength: order.items.length,
        buyer: order.buyer,
        cancelStatus: order.cancelStatus,
        checkoutStatus: order.checkoutStatus,
        createdTime: order.createdTime,
        isTaxIncluded: order.isTaxIncluded,
        taxRate: order.taxRate,
        paymentStatus: order.paymentStatus,
        returnStatus: order.returnStatus,
        refundStatus: order.refundStatus,
        seller: order.seller.userId,
        shipmentTracking: order.shipmentTracking || undefined,
        shippingAddress: order.shippingAddress || undefined,
        shippingService: order.shippingService || undefined,
        createdAt: order.createdAt,
        updatedAt: order.updatedAt
      })),
      total: rawData.totalDocs || rawData.orders.length
    }
  }

  const columns: GridColDef[] = [
    {
      field: "createdAt",
      headerName: translate("created_at"),
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {formatDate(translate("date_format"), params.value as string)}
        </Box>
      )
    },
    {
      field: "orderId",
      headerName: translate("order_id") || "Order ID",
      flex: 1,
      minWidth: 150
    },
    {
      field: "type",
      headerName: translate("marketplace") || "Marketplace",
      flex: 1,
      minWidth: 150
    },
    {
      field: "orderStatus",
      headerName: translate("status"),
      flex: 0.5,
      minWidth: 100
    },
    {
      field: "amountPaid",
      headerName: translate("amount_paid") || "Amount paid",
      flex: 0.7,
      minWidth: 180
    },
    {
      field: "currency",
      headerName: translate("currency") || "Currency",
      flex: 0.7,
      minWidth: 180
    },
    {
      field: "itemsLength",
      headerName: translate("items_length") || "Items Length",
      flex: 0.7,
      minWidth: 180
    },
    {
      field: "actions",
      headerName: translate("actions") || "Actions",
      flex: 0.5,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onSelectOrder(params.row.orderId as string)}
        >
          {translate("view") || "View"}
        </Button>
      )
    }
  ]

  return (
    <Stack sx={{ height: "100%", p: 2, overflow: "hidden", gap: 2 }}>
      <Box sx={{ width: "100%", maxWidth: 400 }}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={translate("search") || "Search..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
              </InputAdornment>
            )
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              bgcolor: "background.paper"
            }
          }}
        />
      </Box>
      <GridTable
        loading={isLoading}
        rows={data?.orders || []}
        columns={columns}
        rowCount={data?.total || 0}
        defaultPageModel={pagination}
        onPaginationModelChange={setPagination}
        getRowId={(row: SanitizedMarketplaceOrder) => row._id}
      />
    </Stack>
  )
}
